import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges, OnDestroy, OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { AudioService } from '../../../services/audio.service';
import { Dc3Planning } from '../../../data/planning/planning';
import { IonTextarea } from '@ionic/angular';
import { windowDebug } from '../../../tools/debug';
import { EntryApi } from '../../../data/entry/entry.api';
import { TopicApi } from '../../../data/topic/topic.api';
import { Tables, TablesInsert } from '../../../data/database.types';
import { ImageApi } from '../../../data/images/image.api';
import { processImage } from '../../../data/images/process-image';
import { debounceTime, Subject, switchMap } from 'rxjs';
import { Topic } from '../../../data/topic/topic';
import { convertBlobToBase64 } from '../../../tools/convert-blob-to-base64';

@Component({
  selector: 'app-entry-editor',
  templateUrl: './entry-editor.component.html',
  styleUrls: ['./entry-editor.component.scss']
})
export class EntryEditorComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput: any;
  @ViewChild(IonTextarea) textArea: IonTextarea | undefined;

  @Input() topic!: Topic;
  @Input() entry: TablesInsert<'entries'> | undefined;
  @Input() date!: string;
  @Input() planning: Dc3Planning | undefined;
  @Input() isFocused = false;

  @Input() rows = 4;
  @Input() showTimer = true;
  @Input() showBookmark = true;
  @Input() showBloglink = true;

  @Output() editorFocused = new EventEmitter<number>();

  localContent: string | null = null;
  localValue: number | null = null;

  timerVisible = false;

  debouncedSave = new Subject<void>();


  pinFormatter = (x: number) => x * 10;

  get isTracker() {
    return !!this.topic.range_target && !!this.topic.is_active_tracker;
  }

  get unsavedChanges() {
    return this.entry?.content !== this.localContent;
  }

  constructor(private topicApi: TopicApi,
              private entryApi: EntryApi,
              private imageApi: ImageApi,
              private audio: AudioService) {
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['entry']?.currentValue) {
      this.localContent = changes['entry'].currentValue.content;
      this.localValue = changes['entry'].currentValue.value;
    }
  }

  ngOnInit() {
    this.debouncedSave.pipe(
      debounceTime(100),
      switchMap(() => this.entryApi.upsert({
        ...this.entry,
        topic_id: this.topic.id,
        date: this.date,
        content: this.localContent,
        value: this.localValue
      }))
    ).subscribe();
  }

  ngOnDestroy() {
    this.debouncedSave.complete();
  }

  onInputBlur() {
    windowDebug('EntryEditor', this);

    if (this.localContent === '') {
      this.localContent = null;
    }

    this.editorFocused.emit(undefined);
    this.updateContentIfChanged()
  }

  @HostListener('window:beforeunload', ['$event'])
  updateContentIfChanged() {
    if (this.unsavedChanges) {
      this.debouncedSave.next();
    }
  }

  showInputAndFocus() {
    this.localContent = '';
    this.audio.plop();
    setTimeout(() => this.textArea?.setFocus());
  }

  toggleTopicIsPinned() {
    this.topicApi.update(this.topic.id, {is_pinned: !this.topic.is_pinned});
    this.audio.plop();
  }


  // async editValueByEvent(event: any) {
  //   const updatedEntry = {
  //     ...this.baseEntry,
  //     value: event.detail.value
  //   };
  //   await this.entryApi.upsert(updatedEntry);
  //   this.audio.plop()
  // }

  // async updateContent(value: string | null) {
  //   const updatedEntry = {
  //     ...this.baseEntry,
  //     content: value
  //   }
  //   await this.entryApi.upsert(updatedEntry);
  // }

  imageProcessing = false;

  addImageToEntry() {
    this.fileInput.nativeElement.click();
  }


  async onPaste(ev: ClipboardEvent) {
    const items = (ev.clipboardData || (ev as any).originalEvent.clipboardData).items;
    let blob = null;
    for (const item of items) {
      if (item.type.indexOf('image') === 0) {
        ev.preventDefault();
        blob = item.getAsFile();
        this.processImage(blob);
        // console.log(blob); // Prints your files
      }
    }
  }

  async onImageChange(ev: any) {
    let file = ev.target.files[0];
    if (!file) {
      return;
    }
    await this.processImage(file);
  }

  async processImage(file: File) {
    this.imageProcessing = true;
    const fileName = await this.imageApi.upload(await processImage(file))
    this.localContent = `${this.localContent ? this.localContent + '\n\n' : ''}![Bild](${fileName})`
    this.imageProcessing = false;
    this.debouncedSave.next();
  }


  async removeEntry() {
    if (this.entry?.id) {
      await this.entryApi.removeEntry(this.entry.id);
      this.localContent = null;
    }
  }
}
